import { AnyAttribute, FeatureDetails, LocationList } from '@ljagis/react-mapping';
import {
  Avatar,
  Box,
  Button,
  Divider,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Theme,
  ThemeProvider,
  Toolbar,
  Typography,
  createMuiTheme,
  makeStyles,
} from '@material-ui/core';
import { blue, green, red } from '@material-ui/core/colors';
import BackIcon from '@material-ui/icons/ArrowBackIosOutlined';
import clsx from 'clsx';
import React, { useState } from 'react';
import { ProjectFeature, ProjectProperties, ProjectType } from 'services/projects';

interface ProjectListProps {
  items: ProjectFeature[];
  onItemSelected?: (item: ProjectFeature | null) => void;
}

const getAttributes = ({
  projectNumber,
  phase,
  estimatedConstructionCost,
  completedDate,
  facility,
  watershed,
  detailsLink,
}: ProjectProperties) => [
  {
    kind: 'text',
    label: 'Project Number',
    value: projectNumber,
  },
  {
    kind: 'text',
    label: 'Project Phase',
    value: phase,
  },
  {
    kind: 'text',
    label: 'Estimated Construction Cost',
    value: estimatedConstructionCost,
  },
  {
    kind: 'text',
    label: 'Completed Date',
    value: completedDate,
  },
  {
    kind: 'text',
    label: 'Facility',
    value: facility,
  },
  {
    kind: 'text',
    label: 'Watershed',
    value: watershed,
  },
  {
    kind: 'link',
    label: 'Details Summary',
    value: detailsLink,
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    height: theme.spacing(4),
    width: theme.spacing(4),
    color: '#FFF',
    border: 'solid 1px rgba(0,0,0,0.7)',
    ...theme.typography.body1,
  },
  red: {
    extend: 'avatar',
    backgroundColor: red[500],
  },
  blue: {
    extend: 'avatar',
    backgroundColor: blue[500],
  },
  green: {
    extend: 'avatar',
    backgroundColor: green[500],
  },
}));

const ProjectIcon: React.FC<{ projectNumber?: string; projectType?: ProjectType }> = ({
  projectNumber,
  projectType,
}) => {
  const classes = useStyles();

  return (
    <Avatar
      className={clsx({
        [classes.green]: projectType === 'Maintenance',
        [classes.blue]: projectType === 'Capital',
        [classes.red]: projectType === 'Grant',
      })}
    >
      {projectNumber}
    </Avatar>
  );
};

const renderLocationListItem = (item: ProjectFeature) => (
  <>
    <ListItemAvatar>
      <ProjectIcon
        projectNumber={item.properties.projectNumber}
        projectType={item.properties.projectType}
      />
    </ListItemAvatar>
    <ListItemText
      primary={item.properties.name}
      secondary={item.properties.phase}
      primaryTypographyProps={{ noWrap: true }}
      secondaryTypographyProps={{ noWrap: true }}
    />
  </>
);

const ProjectsList: React.FC<ProjectListProps> = ({ items, onItemSelected }) => {
  const [single, setSingle] = useState<ProjectFeature | null>(null);

  const {
    maintenance,
    capital,
    grant,
  }: Record<'maintenance' | 'capital' | 'grant', ProjectFeature[]> = {
    maintenance: items.filter((i) => i.properties.projectType === 'Maintenance'),
    capital: items.filter((i) => i.properties.projectType === 'Capital'),
    grant: items.filter((i) => i.properties.projectType === 'Grant'),
  };

  const selectSingle = (item: ProjectFeature | null) => {
    setSingle(item);
    onItemSelected && onItemSelected(item);
  };

  if (single) {
    const attributes = getAttributes(single.properties);

    return (
      <Box display="flex" flexDirection="column" overflow="hidden">
        <Box p={2} pb={2} flex="0 0 auto">
          <Toolbar disableGutters style={{ minHeight: 0 }}>
            <Button
              color="primary"
              startIcon={<BackIcon />}
              style={{ marginLeft: -8, marginTop: -6, marginBottom: -6 }}
              onClick={() => selectSingle(null)}
            >
              Back
            </Button>
            <Box flex={1} />
          </Toolbar>
        </Box>
        <Divider />
        <FeatureDetails
          flex={1}
          p={2}
          pt={2}
          title={single.properties.nameWithProjectNumber}
          // TODO. Allow additional avatar props in FeatureDetails
          // avatar={{
          //   className: clsx({
          //     [classes.green]: projectType === 'Maintenance',
          //     [classes.blue]: projectType === 'Capital',
          //     [classes.red]: projectType === 'Grant',
          //   })
          // }}
          attributes={attributes as AnyAttribute[]}
        />
      </Box>
    );
  }

  return (
    <ThemeProvider
      theme={(theme) =>
        createMuiTheme(theme, {
          props: {
            MuiListItem: {
              dense: true,
            },
          },
        })
      }
    >
      <Box py="1">
        {maintenance.length > 0 && (
          <React.Fragment>
            <ListSubheader disableGutters={true}>
              <Box p={2} bgcolor="#ffffff">
                <Typography variant="h5">Maintenance Projects</Typography>
              </Box>
              <Divider />
            </ListSubheader>
            <LocationList
              items={maintenance}
              renderItem={renderLocationListItem}
              onLocationClick={selectSingle}
            />
          </React.Fragment>
        )}
        {capital.length > 0 && (
          <React.Fragment>
            <ListSubheader disableGutters={true}>
              <Box p={2} bgcolor="#ffffff">
                <Typography variant="h5">Capital Projects</Typography>
              </Box>
              <Divider />
            </ListSubheader>
            <LocationList
              items={capital}
              renderItem={renderLocationListItem}
              onLocationClick={selectSingle}
            />
          </React.Fragment>
        )}
        {grant.length > 0 && (
          <React.Fragment>
            <ListSubheader disableGutters={true}>
              <Box p={2} bgcolor="#ffffff">
                <Typography variant="h5">Grant Projects</Typography>
              </Box>
              <Divider />
            </ListSubheader>
            <LocationList
              items={grant}
              renderItem={renderLocationListItem}
              onLocationClick={selectSingle}
            />
          </React.Fragment>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ProjectsList;
